import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Alert,
  Autocomplete,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Add, Delete, Sports, Warning } from '@mui/icons-material';
import { ReportDraft, MatchEvent, EventType, MatchEventType } from '../../types/matchReport.ts';
import { validateSecondYellow } from '../../utils/matchReportApi.ts';
import unifiedApi from '../../utils/unifiedApi';

interface EventsStepProps {
  draft: ReportDraft;
  onChange: (_updates: Partial<ReportDraft>) => void;
  isHomeTeam?: boolean;
}

export const EventsStep: React.FC<EventsStepProps> = ({ 
  draft, 
  onChange,
  isHomeTeam,
}) => {
  const [newEvent, setNewEvent] = useState<Partial<MatchEvent>>({
    eventType: 'Goal',
  });
  const [eventTypes, setEventTypes] = useState<Array<{ value: EventType; label: string; icon: string }>>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch event types on component mount
  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        const apiEventTypes = await unifiedApi.getMatchEventTypes();
        
        // Map API data to component format with icons
        const mappedEventTypes = apiEventTypes.map((eventType: MatchEventType) => {
          let icon = '⚽'; // default
          let value: EventType;
          
          // Map API names to React EventType values
          switch (eventType.name) {
            case 'Goal':
              value = 'Goal';
              icon = '⚽';
              break;
            case 'Assist':
              // Assists are not recorded as standalone events
              return null;
            case 'Yellow Card':
              value = 'YellowCard';
              icon = '🟨';
              break;
            case 'Red Card':
              value = 'RedCard';
              icon = '🟥';
              break;
            case 'Second Yellow/Red Card':
              value = 'SecondYellowRed';
              icon = '🟨🟥';
              break;
            case 'Clean Sheet':
              value = 'CleanSheet';
              icon = '🛡️';
              break;
            case 'Injury':
              value = 'Injury';
              icon = '🚑';
              break;
            default:
              value = eventType.name as EventType;
              icon = '⚽';
          }
          
          return {
            value: value,
            label: eventType.name,
            icon
          };
        }).filter((item): item is { value: EventType; label: string; icon: string } => item !== null);
        
        setEventTypes(mappedEventTypes);
      } catch (error) {
        console.error('Failed to fetch event types:', error);
        // Fallback to hardcoded types
        setEventTypes([
          { value: 'Goal', label: 'Goal', icon: '⚽' },
          { value: 'YellowCard', label: 'Yellow Card', icon: '🟨' },
          { value: 'RedCard', label: 'Red Card', icon: '🟥' },
          { value: 'SecondYellowRed', label: 'Second Yellow → Red', icon: '🟨🟥' },
          { value: 'CleanSheet', label: 'Clean Sheet', icon: '🛡️' },
          { value: 'Injury', label: 'Injury', icon: '🚑' },
        ]);
      }
    };
    
    fetchEventTypes();
  }, []);

  const playingPlayers = draft.appearances.filter(app => app.played);

  const handleAddEvent = () => {
    if (!newEvent.userTenantId || !newEvent.eventType) {
      return;
    }

    // Validate second yellow card
    if (newEvent.eventType === 'SecondYellowRed') {
      if (!validateSecondYellow(draft.events, newEvent.userTenantId)) {
        alert('Player must have a yellow card first before receiving a second yellow.');
        return;
      }
    }

    const playerName = playingPlayers.find(p => p.userTenantId === newEvent.userTenantId)?.playerName || 'Unknown';

    const event: MatchEvent = {
      id: crypto.randomUUID(),
      eventType: newEvent.eventType,
      userTenantId: newEvent.userTenantId,
      notes: newEvent.notes,
      playerName,
    };

    onChange({
      events: [...draft.events, event]
    });

    // Reset form
    setNewEvent({
      eventType: 'Goal',
    });
  };

  const handleRemoveEvent = (eventId: string) => {
    onChange({
      events: draft.events.filter(event => event.id !== eventId)
    });
  };

  const getEventIcon = (eventType: EventType) => {
    return eventTypes.find(type => type.value === eventType)?.icon || '⚽';
  };

  const getPlayerName = (userTenantId: number) => {
    return playingPlayers.find(p => p.userTenantId === userTenantId)?.playerName || 'Unknown Player';
  };

  const getPlayerOptions = () => {
    return playingPlayers.map(player => ({
      label: player.playerName || `${player.firstName} ${player.lastName}`,
      value: player.userTenantId,
    }));
  };

  const goalCount = draft.events.filter(e => e.eventType === 'Goal').length;
  const cardCount = draft.events.filter(e => ['YellowCard', 'RedCard', 'SecondYellowRed'].includes(e.eventType)).length;
  const injuryCount = draft.events.filter(e => e.eventType === 'Injury').length;

  // Check if your team's goals match the goals recorded as events
  const yourTeamGoals = isHomeTeam ? (draft.homeGoals ?? 0) : (draft.awayGoals ?? 0);
  const opponentGoals = isHomeTeam ? (draft.awayGoals ?? 0) : (draft.homeGoals ?? 0);
  const scoresSet = draft.homeGoals !== null && draft.awayGoals !== null;
  const eventGoals = goalCount;
  const goalsMismatch = scoresSet && yourTeamGoals !== eventGoals && !draft.isForfeit;
  // Clean sheet: opponent scored 0 but reporter hasn't recorded a CleanSheet event yet
  const cleanSheetRequired =
    scoresSet &&
    !draft.isForfeit &&
    opponentGoals === 0 &&
    !draft.events.some(e => e.eventType === 'CleanSheet');

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom align="center">
        Match Events
      </Typography>
      
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Record goals, cards, and other significant events during the match.
        Add details for injuries and cards as needed.
      </Typography>

      {/* Event Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
            <Box>
              <Typography variant="h4" color="primary">
                {goalCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Goals
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="warning.main">
                {cardCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cards
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="error.main">
                {injuryCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Injuries
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Validation Warnings */}
      {goalsMismatch && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            <Typography variant="body2">
              Goal count mismatch: You entered {yourTeamGoals} goal{yourTeamGoals !== 1 ? 's' : ''} for your team in the score,
              but recorded {eventGoals} goal event{eventGoals !== 1 ? 's' : ''}. Please add or remove goal events to match.
            </Typography>
          </Box>
        </Alert>
      )}

      {cleanSheetRequired && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            <Typography variant="body2">
              Your team kept a clean sheet (opponent scored 0 goals). Please add a 🛡️ Clean Sheet event for the goalkeeper or
              the player(s) who earned it before continuing.
            </Typography>
          </Box>
        </Alert>
      )}

      {playingPlayers.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please add players in the previous step before recording events.
        </Alert>
      )}

      {/* Add New Event */}
      {playingPlayers.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Sports />
              Add Event
            </Typography>
            
            <Stack spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel id="event-type-label">Event Type</InputLabel>
                <Select
                  labelId="event-type-label"
                  id="event-type-select"
                  value={newEvent.eventType || 'Goal'}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, eventType: e.target.value as EventType }))}
                  label="Event Type"
                >
                  {eventTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{type.icon}</span>
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                size="small"
                options={getPlayerOptions()}
                getOptionLabel={(option) => option.label}
                value={getPlayerOptions().find(opt => opt.value === newEvent.userTenantId) || null}
                onChange={(_, value) => setNewEvent(prev => ({ ...prev, userTenantId: value?.value }))}
                renderInput={(params) => (
                  <TextField {...params} label="Player" />
                )}
              />

              {(newEvent.eventType === 'YellowCard' || newEvent.eventType === 'RedCard' || 
                newEvent.eventType === 'SecondYellowRed' || newEvent.eventType === 'Injury') && (
                <TextField
                  size="small"
                  fullWidth
                  label="Details (Optional)"
                  value={newEvent.notes || ''}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, notes: e.target.value.slice(0, 200) }))}
                  placeholder="Reason, injury details, etc."
                  inputProps={{ maxLength: 200 }}
                  helperText={`${(newEvent.notes || '').length}/200`}
                />
              )}

              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddEvent}
                disabled={!newEvent.userTenantId || !newEvent.eventType}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Add Event
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Events List */}
      {draft.events.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recorded Events ({draft.events.length})
            </Typography>
            
            {isMobile ? (
              /* Mobile: card list */
              <Stack spacing={1} data-testid="mobile-events-list">
                {draft.events.map((event) => (
                  <Paper key={event.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <span style={{ fontSize: '1.2em', flexShrink: 0 }}>
                        {getEventIcon(event.eventType)}
                      </span>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {eventTypes.find(type => type.value === event.eventType)?.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                          {event.playerName || getPlayerName(event.userTenantId)}
                        </Typography>
                        <Typography variant="caption" color={event.notes ? 'text.secondary' : 'text.disabled'}>
                          {event.notes || 'No details'}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveEvent(event.id!)}
                        color="error"
                        aria-label="Remove event"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            ) : (
              /* Desktop: table */
              <TableContainer data-testid="desktop-events-table">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Event</TableCell>
                      <TableCell>Player</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {draft.events
                      .map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span style={{ fontSize: '1.2em' }}>
                              {getEventIcon(event.eventType)}
                            </span>
                            <Typography variant="body2">
                              {eventTypes.find(type => type.value === event.eventType)?.label}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2">
                            {event.playerName || getPlayerName(event.userTenantId)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          {event.notes ? (
                            <Typography variant="body2" color="text.secondary">
                              {event.notes}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              No details
                            </Typography>
                          )}
                        </TableCell>
                        
                        <TableCell align="center">
                          <Tooltip title="Remove event">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveEvent(event.id!)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {draft.events.length === 0 && playingPlayers.length > 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Sports sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No events recorded yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add goals, cards, and other match events using the form above.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default EventsStep;